// src/components/AuthContext.js
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { trackLoginSuccess, trackOnboardingCompleted } from '../analytics';

const LS_KEY  = 'bzfy_user';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);

  // mantemos um ref pra usar dentro de interceptors
  const didSetupInterceptors = useRef(false);

  /* ——— utilidade p/ axios ——— */
  const setAxiosDefaults = (token) => {
    axios.defaults.baseURL = API_URL;
    if (token) axios.defaults.headers.common.Authorization = 'Bearer ' + token;
    else delete axios.defaults.headers.common.Authorization;
  };

  /* ——— logout ——— */
  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem(LS_KEY);
    setAxiosDefaults(null);
  }, []);

  /* ——— GET /api/me → valida token (robusto) ——— */
  const refreshSession = useCallback(async (token) => {
    try {
      setAxiosDefaults(token);
      const { data } = await axios.get('/api/me'); // server retorna o user

      // se o server devolver null/undefined, tratamos como usuário inexistente
      if (!data || !data._id) throw new Error('USER_NOT_FOUND');

      updateUser({ ...data, token }); // merge + persiste
    } catch (err) {
      // 401, token inválido, usuário deletado, etc.
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  /* ——— restaura usuário salvo ——— */
  useEffect(() => {
    const stored = localStorage.getItem(LS_KEY);
    if (!stored) { setLoading(false); return; }

    try {
      const { token } = JSON.parse(stored);
      if (!token) { setLoading(false); return; }
      refreshSession(token);
    } catch {
      // storage corrompido
      logout();
      setLoading(false);
    }
  }, [refreshSession, logout]);

  /* ——— configura interceptors 401 uma única vez ——— */
  useEffect(() => {
    if (didSetupInterceptors.current) return;
    didSetupInterceptors.current = true;

    const id = axios.interceptors.response.use(
      (resp) => resp,
      (error) => {
        const status = error?.response?.status;
        if (status === 401) {
          // sessão inválida / usuário removido → limpa tudo
          logout();
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [logout]);

  /* ——— login Google ——— */
  const login = async (googleIdToken) => {
    const { data } = await axios.post(`${API_URL}/api/auth/google`, { credential: googleIdToken });
    // data.token vem do server; validamos com /api/me antes de considerar logado
    await refreshSession(data.token);
    // evento GA: login sucesso (provider Google)
    trackLoginSuccess('google');
  };

  /* ——— helpers de atualização local ——— */
  const updateUser = (patch) => {
    setUser((u) => {
      const upd = { ...(u || {}), ...patch };
      localStorage.setItem(LS_KEY, JSON.stringify(upd));
      return upd;
    });
  };

  // Dispara onboarding_completed ao cruzar para >=4
  const updateStage = (newStage) => {
    setUser((u) => {
      const prevStage = Number.isFinite(Number(u?.onboardStage)) ? Number(u.onboardStage) : 0;
      const upd = { ...(u || {}), onboardStage: newStage };
      localStorage.setItem(LS_KEY, JSON.stringify(upd));

      if (prevStage < 4 && Number(newStage) >= 4) {
        const uid = upd._id || upd.id || upd.email || 'anon';
        trackOnboardingCompleted(uid);
      }
      return upd;
    });
  };

  const setOnboarded = () => updateStage(4);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      logout,
      updateUser,
      updateStage,
      setOnboarded,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);